import React, { useState, useEffect } from 'react';
import { Zap, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useResponsive';
import { ListSkeleton } from '../components/ui';

const STATO_COLOR = {
  attivo:   { bg:'var(--green-dim)', color:'var(--green)', dot:'var(--green)' },
  inattivo: { bg:'var(--surface)', color:'var(--text-muted)', dot:'var(--text-dim)' },
  in_pausa: { bg:'var(--yellow-dim)', color:'var(--yellow)', dot:'var(--yellow)' },
};

function HqSec({ emoji, titolo, corpo, mono }) {
  if (!corpo) return null;
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:10.5, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>{emoji} {titolo}</div>
      <div style={{ fontSize:13, lineHeight:1.65, fontFamily: mono ? 'monospace' : undefined }}>{corpo}</div>
    </div>
  );
}

function HqModal({ children, onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,10,18,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:18 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:14, width:'min(580px, calc(100vw - 24px))', maxHeight:'90dvh', overflowY:'auto', padding:'22px 24px', position:'relative', boxShadow:'0 12px 40px rgba(0,0,0,0.3)' }}>
        <button onClick={onClose} style={{ position:'absolute', top:12, right:16, background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:19, lineHeight:1, padding:0, display:'flex' }}>
          <X size={18}/>
        </button>
        {children}
      </div>
    </div>
  );
}

export default function EdenHQ() {
  const [agenti, setAgenti] = useState([]);
  const [playbook, setPlaybook] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lanciando, setLanciando] = useState(null);
  const [lanciato, setLanciato] = useState(null);
  const [selectedPb, setSelectedPb] = useState(null);
  const [selectedAg, setSelectedAg] = useState(null);
  const isMobile  = useIsMobile();
  const navigate  = useNavigate();
  const { user }  = useAuth();
  const isAdmin   = user?.ruolo === 'admin' || user?.ruolo === 'boss';

  useEffect(() => {
    Promise.all([
      api.get('/eden_hq/agenti'),
      api.get('/eden_hq/playbook'),
      api.get('/eden_hq/stats'),
    ]).then(([a, p, s]) => {
      setAgenti(a.data);
      setPlaybook(p.data);
      setStats(s.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const lancia = async (pb) => {
    if (lanciando) return;
    setLanciando(pb.id);
    try {
      await api.post('/eden_hq/lancia', { playbook_id: pb.id });
      setLanciato(pb.id);
      setTimeout(() => setLanciato(null), 3000);
      const r = await api.get('/eden_hq/playbook');
      setPlaybook(r.data);
    } catch(e) {
      alert(e.response?.data?.detail || 'Errore');
    }
    setLanciando(null);
  };

  const agPlaybooks = (ag) => {
    if (ag.playbook_codes) return ag.playbook_codes;
    const nm = (ag.nome || '').toLowerCase();
    return playbook.filter(p => (p.agenti || '').toLowerCase().indexOf(nm) >= 0).map(p => p.codice).join(' · ');
  };

  return (
    <div className="eden-page">
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24, gap:12, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:700, margin:'0 0 4px', color:'var(--text)' }}>Eden HQ</h1>
          <p style={{ fontSize:13, color:'var(--text-muted)', margin:0 }}>Pannello di controllo agenti e playbook.</p>
        </div>
        <button onClick={() => navigate('/gamification')}
          style={{
            display:'flex', alignItems:'center', gap:8,
            padding:'9px 18px', borderRadius:10, border:'none', cursor:'pointer',
            background:'linear-gradient(135deg, #8b5cf6 0%, #06b6d4 100%)',
            color:'white', fontWeight:700, fontSize:13,
            boxShadow:'0 4px 18px rgba(139,92,246,0.35)',
            transition:'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform='scale(1.04)'; e.currentTarget.style.boxShadow='0 6px 24px rgba(139,92,246,0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform='scale(1)';    e.currentTarget.style.boxShadow='0 4px 18px rgba(139,92,246,0.35)'; }}
        >
          🎮 Gamification
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:28, maxWidth:400 }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px' }}>
            <div style={{ fontSize:11.5, color:'var(--text-muted)', marginBottom:4 }}>Agenti</div>
            <div style={{ fontSize:24, fontWeight:700 }}>
              {stats.agenti.totale} <span style={{ fontSize:13, color:'var(--green)', fontWeight:400 }}>· {stats.agenti.attivi} attivi</span>
            </div>
          </div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px' }}>
            <div style={{ fontSize:11.5, color:'var(--text-muted)', marginBottom:4 }}>Playbook disponibili</div>
            <div style={{ fontSize:24, fontWeight:700 }}>{stats.playbook.totale}</div>
          </div>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:24 }}>
        {/* Agenti */}
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Agenti</div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {loading ? (
              <ListSkeleton rows={3} />
            ) : agenti.map(a => {
              const sc = STATO_COLOR[a.stato] || STATO_COLOR.inattivo;
              return (
                <div key={a.id} onClick={() => setSelectedAg(a)} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'13px 16px', display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                  <div style={{ width:36, height:36, borderRadius:'50%', background:'var(--purple-dim)', border:'1px solid rgba(168,85,247,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>🤖</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13.5, fontWeight:600, color:'var(--text)' }}>{a.nome}</div>
                    <div style={{ fontSize:11.5, color:'var(--text-muted)' }}>{a.ruolo}</div>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                    <div style={{ width:7, height:7, borderRadius:'50%', background:sc.dot }}/>
                    <span style={{ fontSize:11, color:sc.color }}>{a.stato}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Playbook */}
        <div>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:12 }}>Playbook</div>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
            {loading ? (
              <ListSkeleton rows={3} />
            ) : playbook.map((pb, i) => (
              <div key={pb.id} onClick={() => setSelectedPb(pb)} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'13px 16px', borderBottom: i < playbook.length-1 ? '1px solid var(--border)' : 'none', cursor:'pointer' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, color:'var(--text)' }}>
                    <span style={{ fontWeight:700 }}>{pb.codice}</span> · {pb.nome}
                  </div>
                  {pb.owner && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:1 }}>Owner: {pb.owner}</div>}
                  {pb.trigger_attivo && (
                    <span style={{ fontSize:10, color:'var(--yellow)', background:'var(--yellow-dim)', padding:'1px 6px', borderRadius:5, marginTop:3, display:'inline-block' }}>
                      ⚡ trigger attivo
                    </span>
                  )}
                  {lanciato === pb.id && (
                    <span style={{ fontSize:10, color:'var(--green)', background:'var(--green-dim)', padding:'1px 6px', borderRadius:5, marginTop:3, display:'inline-block' }}>
                      ✓ Lanciato
                    </span>
                  )}
                </div>
                {isAdmin && (
                  <button onClick={e => { e.stopPropagation(); lancia(pb); }} disabled={!!lanciando}
                    style={{ fontSize:11, color:'var(--cyan)', background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:7, padding:'5px 12px', cursor: lanciando ? 'not-allowed' : 'pointer', whiteSpace:'nowrap', marginLeft:12, opacity: lanciando ? 0.6 : 1, display:'flex', alignItems:'center', gap:5 }}>
                    <Zap size={11}/> {lanciando === pb.id ? 'Lancio…' : 'Lancia →'}
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Spiegazione */}
          <div style={{ marginTop:16, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:10, padding:'13px 16px', fontSize:12, color:'var(--text-muted)' }}>
            <div style={{ fontWeight:600, color:'var(--text)', marginBottom:6 }}>Cosa succede quando premi "Lancia"</div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:7, padding:'7px 10px', flex:1, textAlign:'center', fontSize:11 }}>
                🏛️ EdenFlow<br/><span style={{ color:'var(--text-dim)' }}>innesca trigger</span>
              </div>
              <span style={{ color:'var(--text-dim)' }}>→</span>
              <div style={{ background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:7, padding:'7px 10px', flex:1.5, textAlign:'center', fontSize:11 }}>
                🧠 Claude — cervello + mani<br/><span style={{ color:'var(--cyan)', fontSize:10 }}>raccoglie al polling ed esegue via MCP</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Playbook detail modal */}
      {selectedPb && (
        <HqModal onClose={() => setSelectedPb(null)}>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:4, paddingRight:24 }}>
            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:6, background:'var(--cyan-dim)', color:'var(--cyan)', whiteSpace:'nowrap' }}>PB {selectedPb.codice}</span>
            <h2 style={{ margin:0, fontSize:19, fontWeight:700, color:'var(--text)' }}>{selectedPb.nome}</h2>
          </div>
          {selectedPb.comando && (
            <div style={{ fontFamily:'monospace', fontSize:12, color:'var(--cyan)', marginBottom:16 }}>⌨️ {selectedPb.comando}</div>
          )}
          <HqSec emoji="📖" titolo="Cosa fa" corpo={selectedPb.descrizione} />
          <HqSec emoji="🛠️" titolo="Funzione" corpo={selectedPb.funzione} />
          <HqSec emoji="📦" titolo="Output" corpo={selectedPb.output_atteso} />
          <HqSec emoji="👥" titolo="Agenti coinvolti" corpo={selectedPb.agenti} />
          {isAdmin && (
            <button onClick={e => { e.stopPropagation(); lancia(selectedPb); setSelectedPb(null); }} disabled={!!lanciando}
              style={{ fontSize:12, color:'var(--cyan)', background:'var(--cyan-dim)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:8, padding:'8px 18px', cursor: lanciando ? 'not-allowed' : 'pointer', display:'flex', alignItems:'center', gap:6, marginTop:4 }}>
              <Zap size={13}/> 🚀 Lancia →
            </button>
          )}
        </HqModal>
      )}

      {/* Agente detail modal */}
      {selectedAg && (
        <HqModal onClose={() => setSelectedAg(null)}>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:4, paddingRight:24 }}>
            <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:6, background:'var(--purple-dim)', color:'#c084fc', whiteSpace:'nowrap' }}>{selectedAg.codice}</span>
            <h2 style={{ margin:0, fontSize:19, fontWeight:700, color:'var(--text)' }}>{selectedAg.nome}</h2>
          </div>
          <p style={{ fontSize:12, color:'var(--text-muted)', margin:'0 0 16px' }}>🧰 {selectedAg.ruolo}</p>
          <HqSec emoji="🎭" titolo="Personalità" corpo={selectedAg.personalita} />
          <HqSec emoji="⚡" titolo="Superpower" corpo={selectedAg.superpower} />
          <HqSec emoji="📋" titolo="Playbook attivi" corpo={agPlaybooks(selectedAg) || 'nessun playbook assegnato · attivazione on-demand'} />
        </HqModal>
      )}
    </div>
  );
}
